import React, { useState, useEffect } from "react";
import Services from "../../services/services";
import { toast } from "react-toastify";
import Title from "../../components/Title";
import { useSelector } from "react-redux";
import EditSubPopup from "./EditSubPopup";
import CreateSubPopup from "./CreateSubPopup";
import { RoutePages } from "../../ultils";
import CloneManager from "./Tabs/CloneManager";
import DepositManager from "./Tabs/DepositManager";
import UserManager from "./Tabs/UserManager";
import SubscriptionManager from "./Tabs/SubscriptionManager";

const TABS = {
  CLONE_MANAGER: "CLONE_MANAGER",
  DEPOSIT_MANAGER: "DEPOSIT_MANAGER",
  USER_MANAGER: "USER_MANAGER",
  SUBSCRIPTION_MANAGER: "SUBSCRIPTION_MANAGER",
}

const AdminPanel = () => {
  const authStore = useSelector((state) => state.auth);

  const [dataUsers, setDataUsers] = useState([]);
  const [dataSubscriptions, setDataSubscriptions] = useState([]);
  const [dataDeposits, setDataDeposits] = useState([]);

  // State for edit subscription
  const [isOpenEditSub, setIsOpenEditSub] = useState(false);
  const [originalSub, setOriginalSub] = useState({});
  const [editedSub, setEditedSub] = useState({});

  // State for create subscription
  const [isOpenCreateSub, setIsOpenCreateSub] = useState(false);
  const [newSubUser, setNewSubUser] = useState({});

  const [requestsLimit, setRequestsLimit] = useState(0);
  const [concurrency, setConcurrency] = useState(0);
  const [duration, setDuration] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [isStartedAtNow, setIsStartedAtNow] = useState(null);
  const [generalInfo, setGeneralInfo] = useState(null);
  const [resources, setResources] = useState(null);
  const [openedCard, setOpenedCard] = useState(null); // "deposits" | "users" | "subscriptions" | null

  const username = authStore.user?.username ?? "";
  const password = authStore.user?.password ?? "";

  const fetchDeposits = async () => {
    const res = await Services.Admin.getDeposits(username, password);
    const data = await res.json();
    if (res.ok && data.success) {

      setDataDeposits(data.data);
    } else {
      toast.error(`code_${res.status}: ${data.error}`)
    }
  }
  const fetchUsersSubscriptions = async () => {
    const res = await Services.Admin.getUsers(username, password);
    const data = await res.json();
    if (res.ok && data.success) {
      setDataUsers(data.data);
      setDataSubscriptions(
        data.data.flatMap((user) =>
          user.subscriptions.map((sub) => ({
            ...sub,
            username: user.username,
          }))
        )
      );
    } else {
      toast.error(`code_${res.status}: ${data.error}`)
    }
  }
  const fetchGeneralInfo = async () => {
    const res = await Services.Admin.getGeneralInfo(username, password);
    const data = await res.json();
    if (res.ok && data.success) {
      setGeneralInfo(data.data);
    }
  }
  const fetchResources = async () => {
    const res = await Services.resourcesCheck();
    const data = await res.json();
    if (res.ok && data.success) {
      setResources(data.data);
    }
  }

  // Create Subscription
  const handleOpenCreateSub = (user) => {
    setNewSubUser(user);
    setIsOpenCreateSub(true);
  };
  const handleCloseCreateSub = () => {
    setIsOpenCreateSub(false);
  };
  const handleCreateSubscription = async () => {
    try {
      const result = await Services.Admin.createSubscription(username, password, {
        user_id: newSubUser.id,
        requests_limit: requestsLimit,
        concurrency: concurrency,
        duration: duration,
        started_at: isStartedAtNow ? null : new Date(startedAt).toISOString(),
      });
      const data = await result.json();
      if (result.ok && data.success) {
        toast.success("Subscription created successfully!");
        fetchUsersSubscriptions();
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error("Error creating subscription.");
    }
  };

  // Edit Subscription
  const handleOpenEditSub = (sub) => {
    setOriginalSub(sub);
    setEditedSub({ ...sub });
    setIsOpenEditSub(true);
  };
  const handleCloseEditSub = () => {
    setIsOpenEditSub(false);
  };
  const handleUpdateSubscription = async () => {
    try {
      const result = await Services.Admin.updateSubscription(username, password, originalSub.id, editedSub);
      const data = await result.json();
      if (result.ok && data.success) {
        toast.success("Subscription updated successfully!");
        fetchUsersSubscriptions();
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error("Error updating subscription.");
    }
  };

  const handleDeleteSubscription = async (subId) => {
    if (window.confirm("Are you sure you want to delete this subscription?")) {
      try {
        const result = await Services.Admin.deleteSubscription(username, password, subId);
        const data = await result.json();
        if (result.ok && data.success) {
          toast.success("Subscription deleted successfully!");
          fetchUsersSubscriptions();
        } else {
          toast.error(data.error);
        }
      } catch (error) {
        toast.error("Error deleting subscription.");
      }
    }
  };

  const handleVerifyDeposit = async (deposit) => {
    const res = await Services.Admin.verifyDeposit(username, password, deposit.id);
    const data = await res.json();
    if (res.ok && data.success) {
      toast.success("Verified!");
      fetchDeposits();
    } else {
      toast.error(`code_${res.status}: ${data.error}`)
    }
  }

  useEffect(() => {
    fetchDeposits();
    fetchUsersSubscriptions();
    fetchGeneralInfo();
    fetchResources();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  if (isOpenEditSub)
    return <EditSubPopup
      editedSub={editedSub}
      setEditedSub={setEditedSub}
      handleCloseEditSub={handleCloseEditSub}
      handleUpdateSubscription={handleUpdateSubscription}
      originalSub={originalSub}
    />;

  if (isOpenCreateSub)
    return <CreateSubPopup
      newSubUser={newSubUser}
      requestsLimit={requestsLimit}
      setRequestsLimit={setRequestsLimit}
      concurrency={concurrency}
      setConcurrency={setConcurrency}
      duration={duration}
      setDuration={setDuration}
      startedAt={startedAt}
      setStartedAt={setStartedAt}
      isStartedAtNow={isStartedAtNow}
      setIsStartedAtNow={setIsStartedAtNow}
      handleCloseCreateSub={handleCloseCreateSub}
      handleCreateSubscription={handleCreateSubscription}
    />;

  return (
    <div className="p-2 sm:p-4 w-full mx-auto">
      <Title page={RoutePages.ADMIN} title="ADMIN" />

      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {/* Card 1: Tổng nợ */}
          {generalInfo &&
            <div className="flex flex-col bg-white rounded-lg shadow p-6 min-w-[220px]">
              <div className="text-lg font-bold mb-2">Tổng nợ</div>
              <div className="text-2xl text-blue-700 font-extrabold mb-1">
                {((generalInfo.users?.total_balance + generalInfo.subscriptions?.paid?.total_money) * 25000).toLocaleString()}đ
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-semibold">Nợ balance: </span>
                <span className="text-blue-700 font-bold">${(generalInfo.users?.total_balance).toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-semibold">Nợ Subs: </span>
                <span className="text-blue-700 font-bold">{generalInfo.subscriptions?.paid?.total_money?.toLocaleString()}</span>
              </div>
            </div>
          }

          {/* Card 2: Resources */}
          {resources &&
            <div
              className={`flex flex-col bg-white rounded-lg shadow p-6 min-w-[220px] cursor-pointer border-2 ${openedCard === TABS.CLONE_MANAGER ? "border-blue-500" : "border-transparent"}`}
              onClick={() => setOpenedCard(openedCard === TABS.CLONE_MANAGER ? null : TABS.CLONE_MANAGER)}
            >
              <div className="text-lg font-bold mb-2">Resources</div>
              <div className="text-gray-700">
                <span className="font-semibold">EAAAAU : </span>
                <span className="text-blue-700 font-bold">
                  {(resources.EAAAAU_LIVE).toLocaleString()} LIVE - {(resources.EAAAAU_WAIT).toLocaleString()} WAIT
                </span>
              </div>
              <div className="text-gray-700">
                <span className="font-semibold">EAAGNO : </span>
                <span className="text-blue-700 font-bold">
                  {(resources.EAAGNO_LIVE).toLocaleString()} LIVE - {(resources.EAAGNO_WAIT).toLocaleString()} WAIT
                </span>
              </div>
              <div className="text-gray-700">
                <span className="font-semibold">RECOVERY </span>
                <span className="text-blue-700 font-bold">{(resources.NEED_RECOVERY).toLocaleString()}</span>

                <span className="font-semibold"> COST </span>
                <span className="text-blue-700 font-bold">{(resources.COST).toLocaleString()}</span>
              </div>
              <div className="text-gray-700">
              </div>
            </div>
          }

          {/* Card 3: Deposits */}
          {dataDeposits &&
            <div
              className={`flex flex-col bg-white rounded-lg shadow p-6 min-w-[220px] cursor-pointer border-2 ${openedCard === TABS.DEPOSIT_MANAGER ? "border-blue-500" : "border-transparent"}`}
              onClick={() => setOpenedCard(openedCard === TABS.DEPOSIT_MANAGER ? null : TABS.DEPOSIT_MANAGER)}
            >
              <div className="text-lg font-bold mb-2">Deposits</div>
              <div className="flex items-end gap-2 mb-1">
                <span className="text-2xl text-blue-700 font-extrabold">{dataDeposits.length}</span>
                <span className="text-gray-500 text-sm">total</span>
              </div>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                  Pending: <span className="font-bold">{dataDeposits.filter(x => x.status === 0).length}</span>
                </span>
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  Verified: <span className="font-bold">{dataDeposits.filter(x => x.status === 1).length}</span>
                </span>
              </div>
            </div>
          }

          {/* Card 4: Users */}
          {dataUsers &&
            <div
              className={`flex flex-col bg-white rounded-lg shadow p-6 min-w-[220px] cursor-pointer border-2 ${openedCard === TABS.USER_MANAGER ? "border-blue-500" : "border-transparent"}`}
              onClick={() => setOpenedCard(openedCard === TABS.USER_MANAGER ? null : TABS.USER_MANAGER)}
            >
              <div className="text-lg font-bold mb-2">Users</div>
              <div className="flex items-end gap-2 mb-1">
                <span className="text-2xl text-blue-700 font-extrabold">{dataUsers.length}</span>
                <span className="text-gray-500 text-sm">total</span>
              </div>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  Verified: <span className="font-bold">{dataUsers.filter(x => x.is_verified === 1).length}</span>
                </span>
              </div>
            </div>
          }

          {/* Card 5: Subscriptions */}
          {dataSubscriptions &&
            <div
              className={`flex flex-col bg-white rounded-lg shadow p-6 min-w-[220px] cursor-pointer border-2 ${openedCard === TABS.SUBSCRIPTION_MANAGER ? "border-blue-500" : "border-transparent"}`}
              onClick={() => setOpenedCard(openedCard === TABS.SUBSCRIPTION_MANAGER ? null : TABS.SUBSCRIPTION_MANAGER)}
            >
              <div className="text-lg font-bold mb-2">Subscriptions</div>
              <div className="flex items-end gap-2 mb-1">
                <span className="text-2xl text-blue-700 font-extrabold">{dataSubscriptions.length}</span>
                <span className="text-gray-500 text-sm">total</span>
              </div>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  Freemium: <span className="font-bold">{dataSubscriptions.filter(x => x.is_freemium === 1).length}</span>
                </span>
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  Paid: <span className="font-bold">{dataSubscriptions.filter(x => x.is_freemium === 0).length}</span>
                </span>
              </div>
            </div>
          }
        </div>

        {openedCard === TABS.CLONE_MANAGER &&
          <CloneManager />
        }
        {openedCard === TABS.DEPOSIT_MANAGER &&
          <DepositManager dataDeposits={dataDeposits} handleVerifyDeposit={handleVerifyDeposit} />
        }
        {openedCard === TABS.USER_MANAGER &&
          <UserManager dataUsers={dataUsers} handleOpenCreateSub={handleOpenCreateSub} />
        }
        {openedCard === TABS.SUBSCRIPTION_MANAGER &&
          <SubscriptionManager dataSubscriptions={dataSubscriptions} handleOpenEditSub={handleOpenEditSub} handleDeleteSubscription={handleDeleteSubscription} />
        }
      </div>
    </div>
  );
};

export default AdminPanel;
